import React, { useEffect, useState } from "react";
import { Loader2, Send, Share2, Linkedin, Facebook, Instagram, Store, CheckCircle2, Image as ImageIcon, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import ImageUpload from "./ImageUpload";
import HashtagSetsPicker from "./HashtagSetsPicker";
import NetworkPreview from "./NetworkPreview";
import { useLinkPreview, firstUrl } from "./useLinkPreview";
import { adminApi } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";

const NETWORKS = [
  { key: "linkedin", label: "LinkedIn", icon: Linkedin },
  { key: "facebook", label: "Facebook", icon: Facebook },
  { key: "instagram", label: "Instagram", icon: Instagram },
  { key: "google", label: "Google Business", icon: Store },
];

// default: 1 hour from now, formatted for <input type=datetime-local>
function defaultWhen() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function MetricoolComposer({ open, onClose, initial }) {
  const { toast } = useToast();
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [networks, setNetworks] = useState(["linkedin", "facebook"]);
  const [when, setWhen] = useState(defaultWhen());
  const [draft, setDraft] = useState(true);
  const [firstComment, setFirstComment] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [tone, setTone] = useState("professional");
  const [bestTimes, setBestTimes] = useState(null);

  useEffect(() => {
    if (open && !bestTimes) adminApi.metricool.bestTimes().then((d) => setBestTimes(d.networks)).catch(() => {});
  }, [open]); // eslint-disable-line

  const genCaption = async (silent = false) => {
    setAiLoading(true);
    try {
      const { caption } = await adminApi.metricool.caption({
        kind: initial?.kind || "post",
        title: initial?.title || "",
        summary: initial?.summary || (initial?.title ? "" : text),
        link: initial?.link || "",
        networks,
        tone,
      });
      if (caption) setText(caption);
    } catch (e) {
      if (!silent) toast({ title: "Couldn't draft a caption", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setAiLoading(false); }
  };

  useEffect(() => {
    if (open) {
      setText(initial?.text || "");
      setImageUrl(initial?.image_url || "");
      setNetworks(initial?.networks || ["linkedin", "facebook"]);
      setWhen(defaultWhen());
      setDraft(true);
      setFirstComment("");
      setDone(false);
      // Auto-draft a smart caption when launched from a content item.
      if (initial?.autogen && initial?.title) genCaption(true);
    }
  }, [open]); // eslint-disable-line

  const toggle = (k) => setNetworks((n) => (n.includes(k) ? n.filter((x) => x !== k) : [...n, k]));

  const insertHashtags = (tags) => setText((t) => (t.trim() ? `${t.trim()}\n\n${tags}` : tags));

  const igNoImage = networks.includes("instagram") && !imageUrl;
  const gbpLimit = networks.includes("google") && text.length > 1500;
  const linkPreview = useLinkPreview(text, !!imageUrl);
  const linkNoImage = !imageUrl && !!firstUrl(text);

  const submit = async () => {
    if (!text.trim()) { toast({ title: "Add some post text", variant: "destructive" }); return; }
    if (!networks.length) { toast({ title: "Pick at least one network", variant: "destructive" }); return; }
    if (igNoImage) { toast({ title: "Instagram needs an image", description: "Attach an image or deselect Instagram.", variant: "destructive" }); return; }
    setSending(true);
    try {
      await adminApi.metricool.post({
        text: text.trim(),
        image_url: imageUrl || "",
        networks,
        date_time: `${when}:00`,
        timezone: "Europe/London",
        draft,
        first_comment: firstComment.trim(),
        source: initial?.source || "custom",
        source_id: initial?.source_id || "",
      });
      setDone(true);
    } catch (e) {
      toast({ title: "Couldn't send to Metricool", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="metricool-composer">
        {done ? (
          <div className="text-center py-6" data-testid="metricool-success">
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 mb-5"><CheckCircle2 size={34} /></span>
            <DialogTitle className="font-display text-navy text-2xl">{draft ? "Draft sent to Metricool" : "Post scheduled in Metricool"}</DialogTitle>
            <DialogDescription className="text-slate-500 mt-3 leading-relaxed">
              {draft
                ? "It's waiting as a draft in your Metricool planner — open Metricool to review, tweak per-network and publish."
                : "It's scheduled in your Metricool planner. You can still adjust it there before it goes out."}
            </DialogDescription>
            <Button onClick={onClose} data-testid="metricool-close" className="mt-6 bg-navy hover:bg-navy-deep text-white font-bold rounded-full px-8">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-navy text-2xl flex items-center gap-2"><Share2 size={20} className="text-orange" /> Send to Metricool</DialogTitle>
              <DialogDescription className="text-slate-500">Compose a social post. It'll land in your Metricool planner where you can fine-tune each network.</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-2">
              <div>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Label className="text-navy font-semibold">Post text</Label>
                  <div className="flex items-center gap-2">
                    <HashtagSetsPicker onInsert={insertHashtags} />
                    <select data-testid="metricool-tone" value={tone} onChange={(e) => setTone(e.target.value)} title="AI caption tone" className="text-xs font-semibold rounded-full border border-slate-200 px-2.5 py-1.5 bg-white cursor-pointer focus:outline-none">
                      <option value="professional">Professional</option>
                      <option value="playful">Playful</option>
                      <option value="punchy">Punchy</option>
                    </select>
                    <button type="button" data-testid="metricool-ai-caption" onClick={() => genCaption(false)} disabled={aiLoading} className="inline-flex items-center gap-1.5 text-xs font-bold text-orange hover:text-orange-dark disabled:opacity-50">
                      {aiLoading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {text ? "Regenerate" : "Draft with AI"}
                    </button>
                    <span className={`text-xs ${gbpLimit ? "text-red-500 font-semibold" : "text-slate-400"}`}>{text.length}</span>
                  </div>
                </div>
                <Textarea data-testid="metricool-text" value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder="Write your caption, or tap 'Draft with AI' for a punchy starting point." className="mt-1.5" />
                {gbpLimit && <p className="text-xs text-red-500 mt-1">Google Business allows max 1,500 characters.</p>}
              </div>

              <div>
                <Label className="text-navy font-semibold mb-1.5 block">Networks</Label>
                <div className="flex flex-wrap gap-2">
                  {NETWORKS.map((n) => {
                    const Icon = n.icon;
                    const on = networks.includes(n.key);
                    return (
                      <button key={n.key} type="button" data-testid={`metricool-net-${n.key}`} onClick={() => toggle(n.key)}
                        className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-2 rounded-full border transition-colors ${on ? "bg-navy text-white border-navy" : "bg-white text-navy border-slate-200 hover:border-navy/30"}`}>
                        <Icon size={15} /> {n.label}
                      </button>
                    );
                  })}
                </div>
                {igNoImage && <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1"><ImageIcon size={12} /> Instagram needs an image attached.</p>}
              </div>

              <div>
                <Label className="text-navy font-semibold">Image (optional)</Label>
                <div className="mt-1.5"><ImageUpload value={imageUrl} onChange={setImageUrl} hint="Recommended for Facebook, LinkedIn & Google Business. Required for Instagram." /></div>
              </div>

              {linkNoImage && (
                <div data-testid="metricool-image-reminder" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                  <ImageIcon size={14} className="shrink-0 mt-0.5" />
                  <span>No image attached — LinkedIn/Facebook will show a link-preview card using the linked page's share image (see the Preview below). Attach an image to post your own picture instead.</span>
                </div>
              )}

              {networks.length > 0 && (
                <div>
                  <Label className="text-navy font-semibold mb-1.5 block">Preview</Label>
                  <NetworkPreview text={text} image={imageUrl} networks={networks} linkPreview={linkPreview} />
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-navy font-semibold">Date &amp; time</Label>
                  <input type="datetime-local" data-testid="metricool-when" value={when} onChange={(e) => setWhen(e.target.value)} className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40" />
                  {bestTimes && networks.length > 0 && (
                    <div data-testid="metricool-best-time" className="text-xs text-slate-500 mt-1.5 space-y-0.5">
                      <p className="font-semibold text-slate-400">💡 Suggested times (UK):</p>
                      {networks.map((n) => bestTimes[n] && (
                        <p key={n}><span className="font-semibold text-navy">{NETWORKS.find((x) => x.key === n)?.label || n}:</span> {bestTimes[n].label}</p>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm font-semibold text-navy pb-2">
                    <input type="checkbox" data-testid="metricool-draft" checked={draft} onChange={(e) => setDraft(e.target.checked)} className="w-4 h-4 accent-orange" />
                    Send as draft (review in Metricool)
                  </label>
                </div>
              </div>

              <div>
                <Label className="text-navy font-semibold">First comment (optional)</Label>
                <Textarea data-testid="metricool-firstcomment" value={firstComment} onChange={(e) => setFirstComment(e.target.value)} rows={2} placeholder="e.g. hashtags or a follow-up link posted as the first comment" className="mt-1.5" />
              </div>

              <Button onClick={submit} disabled={sending} data-testid="metricool-submit" className="w-full bg-orange hover:bg-orange-dark text-white font-bold rounded-full py-6">
                {sending ? <><Loader2 size={16} className="mr-2 animate-spin" /> Sending…</> : <>{draft ? "Send draft to Metricool" : "Schedule in Metricool"} <Send size={16} className="ml-2" /></>}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Per-row button used in the blog list to push an article to Metricool.
export function MetricoolPostButton({ item }) {
  const [open, setOpen] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  // Share endpoint gives social crawlers the article's own OG image (the SPA
  // can't). Falls back to the plain page if there's no slug.
  const link = item.slug ? `${origin}/api/share/blog/${item.slug}` : origin;
  const caption = `${item.title || ""}\n\n${item.excerpt ? item.excerpt + "\n\n" : ""}Read more: ${link}`.trim();
  return (
    <>
      <button
        data-testid={`metricool-post-btn-${item.id}`}
        onClick={() => setOpen(true)}
        title="Send this article to Metricool"
        className="h-9 px-3 rounded-lg flex items-center gap-1.5 text-xs font-semibold transition-colors bg-slate-100 text-navy hover:bg-slate-200"
      >
        <Share2 size={14} /> Metricool
      </button>
      <MetricoolComposer open={open} onClose={() => setOpen(false)} initial={{ text: caption, title: item.title || "", summary: item.excerpt || "", link, kind: "blog", autogen: true, image_url: item.image || "", source: "blog", source_id: item.id }} />
    </>
  );
}
